import React from 'react';
import { CalendarManagementConfig } from './CalendarManagementConfig';
import { SectionCard } from './SectionCard';
import { EscalationConfigSection } from './EscalationConfigSection';
import { OfficialInfoSection } from './OfficialInfoSection';
import { LeadManagementConfig } from './LeadManagementConfig';
import { MediaMessagingConfig } from './MediaMessagingConfig';
import { CustomActionsSelector, CustomActionsList } from '@/modules/automation/custom-actions';
import { AgentSkillsSelector } from '@/modules/skills/components/AgentSkillsSelector';
import { useVoicesDropdown } from '@/modules/automation/voices/hooks';
import type { CreateAIAgentRequest } from '../services/agentsApi';
import type { AIAgentCapabilityConfig } from '@troia-v3/types';

interface ToolsTabProps {
  formData: Partial<CreateAIAgentRequest>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateAIAgentRequest>>>;
  capabilityConfigs: AIAgentCapabilityConfig[];
  onCapabilityConfigChange: (config: AIAgentCapabilityConfig) => void;
}

export function ToolsTab({
  formData,
  setFormData,
  capabilityConfigs,
  onCapabilityConfigChange,
}: ToolsTabProps) {
  const { data: voicesDropdown } = useVoicesDropdown();

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Destino das transferências (escalationConfig) */}
      <EscalationConfigSection formData={formData} setFormData={setFormData} />

      {/* Cadastro oficial do negócio (officialInfoOverride, Fase 3) */}
      <OfficialInfoSection formData={formData} setFormData={setFormData} />

      {/* Capacidades Avançadas */}
      <SectionCard
        icon="settings"
        title="Capacidades Avançadas"
        subtitle="Configure capacidades especiais que o agente pode utilizar durante o atendimento"
      >
        {/* Calendar Management Capability */}
        <CalendarManagementConfig
          config={capabilityConfigs.find(c => c.capability === 'calendar_management')}
          onChange={onCapabilityConfigChange}
        />

        {/* Lead Management Capability */}
        <LeadManagementConfig
          config={capabilityConfigs.find(c => c.capability === 'lead_management')}
          onChange={onCapabilityConfigChange}
        />

        {/* Media Messaging Capability */}
        <MediaMessagingConfig
          config={capabilityConfigs.find(c => c.capability === 'media_messaging')}
          onChange={onCapabilityConfigChange}
        />

        {/* Envio de áudio (voz) — capacidade dentro de Capacidades Avançadas */}
        <div
          className="rounded-lg p-4"
          style={{
            backgroundColor: 'var(--theme-input-background)',
            border: '1px solid var(--theme-input-border)',
          }}
        >
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={formData.voiceEnabled || false}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  voiceEnabled: e.target.checked,
                  voiceConfig: e.target.checked ? formData.voiceConfig : undefined,
                });
              }}
              className="w-4 h-4 rounded"
              style={{ accentColor: 'var(--theme-primary)' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Habilitar envio de áudio
            </span>
          </label>

          {formData.voiceEnabled && (
            <div>
              <label
                className="block text-xs font-medium mb-1.5"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Selecionar Voz
              </label>
              {voicesDropdown && voicesDropdown.length > 0 ? (
                <select
                  value={formData.voiceConfig?.voiceId || ''}
                  onChange={(e) => {
                    const selectedVoice = voicesDropdown.find(v => v.id === e.target.value);
                    setFormData({
                      ...formData,
                      voiceConfig: selectedVoice
                        ? { aiProviderId: selectedVoice.providerId, voiceId: selectedVoice.id }
                        : undefined,
                    });
                  }}
                  className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                  style={{
                    backgroundColor: 'var(--theme-input-background)',
                    borderColor: 'var(--theme-input-border)',
                    color: 'var(--theme-input-text)',
                  }}
                >
                  <option value="">Selecione uma voz...</option>
                  {voicesDropdown.map((voice) => (
                    <option key={voice.id} value={voice.id}>
                      {voice.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhuma voz disponível.{' '}
                  <a
                    href="/automation/voices/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: 'var(--theme-primary)' }}
                  >
                    Crie uma voz
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      </SectionCard>

      {/* Ferramentas & Skills */}
      <SectionCard
        icon="tool"
        title="Ferramentas & Skills"
        subtitle="Ferramentas executam ações personalizadas e integram com APIs externas; skills são workflows invocáveis pelo agente durante o atendimento"
      >
        {/* Lista de Actions Selecionadas */}
        <div className="mb-6">
          <CustomActionsList
            selectedActionIds={formData.customActionIds || []}
            onRemove={(actionId) => {
              const updatedIds = (formData.customActionIds || []).filter(id => id !== actionId);
              setFormData({ ...formData, customActionIds: updatedIds });
            }}
            onViewDetails={(actionId) => {
              // Abrir em nova aba para não perder o formulário
              window.open(`/automation/custom-actions/${actionId}/edit`, '_blank');
            }}
          />
        </div>

        {/* Seletor para Adicionar Mais Actions */}
        <div>
          <label
            className="block text-xs font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Adicionar Ferramentas
          </label>
          <CustomActionsSelector
            selectedActionIds={formData.customActionIds || []}
            onSelectionChange={(actionIds) => setFormData({ ...formData, customActionIds: actionIds })}
            label=""
            description=""
          />
        </div>

        {/* Skills — workflows invocáveis, mesmo card */}
        <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
          <label
            className="block text-xs font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Skills (workflows invocáveis)
          </label>
          <AgentSkillsSelector
            selectedSkillIds={formData.skillWorkflowIds || []}
            onChange={(skillIds) =>
              setFormData({ ...formData, skillWorkflowIds: skillIds })
            }
          />
        </div>
      </SectionCard>
    </div>
  );
}
